import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth, firestore } from "../../config/FirebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import FetchGathering from "../../api/FetchGathering";
import FetchEvents from "../../api/FetchEvents";
import FetchAnnouncements from "../../api/FetchAnnouncements";
import FetchMapImage from "../../api/FetchMapImage";
import FetchLogoImage from "../../api/FetchLogoImage";
import FetchParticipant from "../../api/FetchParticipant";
import toCamelCase from "../../logic/toCamelCase";
import { formatDate } from "../../logic/formatDate";
import itiniriLogoLight from "../../images/itiniri_logo_light.png";

const APP_BG = "#cfeaf5";
const CARD_BG = "#238ab2";
const DARK = "#17485b";
const TEXT = "#0f4154";
const WHITE = "#eef8fc";
const BORDER = "rgba(23, 72, 91, 0.28)";
const SHADOW = "0 8px 24px rgba(16, 52, 66, 0.2)";

function normalizeArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return Object.values(value);
  return [];
}

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function formatTimeLabel(timeValue) {
  if (!timeValue) return "";
  const raw = String(timeValue).trim();

  if (/am|pm/i.test(raw)) {
    return raw.toUpperCase();
  }

  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return raw;

  let hours = Number(match[1]);
  const minutes = match[2];
  const suffix = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${suffix}`;
}

function formatEventDateHeading(dateValue) {
  if (!dateValue) return "Schedule";
  const date = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(date.getTime())) return safeText(dateValue, "Schedule");

  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function buildEventTimeRange(event) {
  const start = formatTimeLabel(
    event.startTime || event.time || event.eventTime || event.beginTime,
  );
  const end = formatTimeLabel(event.endTime || event.finishTime);

  if (start && end) return `${start} - ${end}`;
  return start || end || "";
}

function initialsFromName(name) {
  const parts = safeText(name, "")
    .split(" ")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) return "IT";

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

function IconMegaphone({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path
        d="M4 14V10C4 8.9 4.9 8 6 8H8L16 4V20L8 16H6C4.9 16 4 15.1 4 14Z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M9 16L10.3 20"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconInfo({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.8" />
      <path
        d="M12 10V16"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="12" cy="7.2" r="1.2" fill={color} />
    </svg>
  );
}

function IconCalendar({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <rect
        x="4"
        y="6"
        width="16"
        height="14"
        rx="2.5"
        stroke={color}
        strokeWidth="1.8"
      />
      <path
        d="M8 4V8M16 4V8M4 10H20"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconMap({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path
        d="M4 6L9 4L15 6L20 4V18L15 20L9 18L4 20V6Z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M9 4V18M15 6V20" stroke={color} strokeWidth="1.8" />
    </svg>
  );
}

function IconGear({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path
        d="M12 8.4A3.6 3.6 0 1 1 12 15.6A3.6 3.6 0 0 1 12 8.4Z"
        stroke={color}
        strokeWidth="1.8"
      />
      <path
        d="M19 12C19 11.5 18.96 11.01 18.88 10.54L21 8.9L18.9 5.3L16.32 6.28C15.6 5.72 14.78 5.28 13.88 4.99L13.5 2H10.5L10.12 4.99C9.22 5.28 8.4 5.72 7.68 6.28L5.1 5.3L3 8.9L5.12 10.54C5.04 11.01 5 11.5 5 12C5 12.5 5.04 12.99 5.12 13.46L3 15.1L5.1 18.7L7.68 17.72C8.4 18.28 9.22 18.72 10.12 19.01L10.5 22H13.5L13.88 19.01C14.78 18.72 15.6 18.28 16.32 17.72L18.9 18.7L21 15.1L18.88 13.46C18.96 12.99 19 12.5 19 12Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconArrowLeft({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path
        d="M15 5L8 12L15 19"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconStar({ size = 24, color = "currentColor", filled = false }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={filled ? color : "none"}
    >
      <path
        d="M12 3.8L14.47 8.8L20 9.6L16 13.5L16.94 19L12 16.4L7.06 19L8 13.5L4 9.6L9.53 8.8L12 3.8Z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSearch({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <circle cx="10.5" cy="10.5" r="5.8" stroke={color} strokeWidth="1.8" />
      <path
        d="M15.2 15.2L20 20"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconChevronDown({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path
        d="M6 9L12 16L18 9"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconChevronUp({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path
        d="M6 15L12 8L18 15"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconClose({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path
        d="M6 6L18 18M18 6L6 18"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconEnvelope({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <rect
        x="4"
        y="6"
        width="16"
        height="12"
        rx="2"
        stroke={color}
        strokeWidth="1.8"
      />
      <path
        d="M5 8L12 13L19 8"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconGlobe({ size = 24, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.8" />
      <path
        d="M3 12H21M12 3C14.8 5.8 16.4 9 16.4 12C16.4 15 14.8 18.2 12 21M12 3C9.2 5.8 7.6 9 7.6 12C7.6 15 9.2 18.2 12 21"
        stroke={color}
        strokeWidth="1.4"
      />
    </svg>
  );
}

function PhoneShell({ children }) {
  return (
    <div
      style={{
        width: 390,
        maxWidth: "92vw",
        height: 800,
        maxHeight: "88vh",
        borderRadius: 34,
        background: "#1a1a1a",
        padding: 10,
        boxShadow: "0 28px 80px rgba(0,0,0,0.45)",
        border: "1px solid rgba(255,255,255,0.12)",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 26,
          overflow: "hidden",
          background: APP_BG,
          position: "relative",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ScreenScroller({ children, bottomInset = 96 }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        overflowY: "auto",
        overflowX: "hidden",
        paddingBottom: bottomInset,
        background: APP_BG,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
      className="app-preview-screen-scroller"
    >
      {children}
    </div>
  );
}

function BottomTabs({ currentTab, onTab }) {
  const items = [
    { key: "updates", label: "Updates", icon: <IconMegaphone size={22} /> },
    { key: "info", label: "Info", icon: <IconInfo size={22} /> },
    { key: "schedule", label: "Schedule", icon: <IconCalendar size={22} /> },
    { key: "map", label: "Map", icon: <IconMap size={22} /> },
    { key: "settings", label: "Settings", icon: <IconGear size={22} /> },
  ];

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 88,
        background: APP_BG,
        borderTop: `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "space-between",
        padding: "8px 6px 14px",
        zIndex: 20,
      }}
    >
      {items.map((item) => {
        const active = item.key === currentTab;

        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onTab(item.key)}
            style={{
              flex: 1,
              minWidth: 0,
              appearance: "none",
              border: 0,
              background: "transparent",
              color: active ? TEXT : "#2b7da0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              padding: 0,
              fontSize: 10,
              lineHeight: 1.1,
              fontWeight: active ? 700 : 500,
              textDecoration: active ? "underline" : "none",
              textUnderlineOffset: 4,
              cursor: "pointer",
            }}
          >
            <div
              style={{
                width: 26,
                height: 26,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {item.icon}
            </div>
            <span
              style={{
                display: "block",
                width: "100%",
                textAlign: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PhoneHeader({ title, onBack, rightContent = null, titleSize = 29 }) {
  return (
    <div style={{ padding: "6px 16px 0" }}>
      <div
        style={{
          position: "relative",
          minHeight: 52,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: 52,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            zIndex: 2,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            style={{
              width: 40,
              height: 40,
              border: 0,
              padding: 0,
              margin: 0,
              background: "transparent",
              color: "#2b7da0",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconArrowLeft size={26} color="#2b7da0" />
          </button>
        </div>

        <div
          style={{
            width: "100%",
            textAlign: "center",
            color: "#2380aa",
            fontWeight: 800,
            fontSize: titleSize,
            letterSpacing: -0.4,
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            paddingLeft: 56,
            paddingRight: 56,
            boxSizing: "border-box",
          }}
        >
          {title}
        </div>

        <div
          style={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            minWidth: 52,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 6,
            zIndex: 2,
          }}
        >
          {rightContent}
        </div>
      </div>
    </div>
  );
}

function HomeTile({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        minWidth: 0,
        minHeight: 98,
        borderRadius: 12,
        border: 0,
        background: CARD_BG,
        color: WHITE,
        fontSize: 26,
        fontWeight: 400,
        boxShadow: SHADOW,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      {label}
    </button>
  );
}

function AnnouncementCard({ item, compact = false, onClick }) {
  const cardProps = {
    width: "100%",
    textAlign: "left",
    background: CARD_BG,
    border: 0,
    borderRadius: 14,
    color: WHITE,
    boxShadow: SHADOW,
    padding: compact ? "14px 16px" : "16px 20px 20px",
  };

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{ ...cardProps, cursor: "pointer" }}
      >
        {compact ? (
          <>
            <div style={{ color: "#f0d24f", fontSize: 16, marginBottom: 6 }}>
              Announcements
            </div>
            <div
              style={{
                fontSize: 29,
                lineHeight: 1.08,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginBottom: 8,
              }}
            >
              {safeText(item.title || item.announcementTitle || "Announcement")}
            </div>
            <div
              style={{
                fontSize: 16,
                opacity: 0.95,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {safeText(item.description || item.text || item.message || "")}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "flex-start",
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 18, opacity: 0.95 }}>Latest</div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>
                {item.createdAtLabel || item.dateLabel || ""}
              </div>
            </div>

            <div
              style={{
                fontSize: 29,
                lineHeight: 1.12,
                marginBottom: 14,
                fontWeight: 500,
              }}
            >
              {safeText(item.title || item.announcementTitle || "Announcement")}
            </div>

            <div style={{ fontSize: 18, opacity: 0.95 }}>
              {safeText(item.description || item.text || item.message || "")}
            </div>
          </>
        )}
      </button>
    );
  }

  return (
    <div style={cardProps}>
      {compact ? (
        <>
          <div style={{ color: "#f0d24f", fontSize: 16, marginBottom: 6 }}>
            Announcements
          </div>
          <div
            style={{
              fontSize: 29,
              lineHeight: 1.08,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginBottom: 8,
            }}
          >
            {safeText(item.title || item.announcementTitle || "Announcement")}
          </div>
          <div
            style={{
              fontSize: 16,
              opacity: 0.95,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {safeText(item.description || item.text || item.message || "")}
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "flex-start",
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 18, opacity: 0.95 }}>Latest</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {item.createdAtLabel || item.dateLabel || ""}
            </div>
          </div>

          <div
            style={{
              fontSize: 29,
              lineHeight: 1.12,
              marginBottom: 14,
              fontWeight: 500,
            }}
          >
            {safeText(item.title || item.announcementTitle || "Announcement")}
          </div>

          <div style={{ fontSize: 18, opacity: 0.95 }}>
            {safeText(item.description || item.text || item.message || "")}
          </div>
        </>
      )}
    </div>
  );
}

function ContactIconButton({ icon, label }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
      }}
    >
      <div
        style={{
          width: 58,
          height: 58,
          borderRadius: "50%",
          background: DARK,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: WHITE,
        }}
      >
        {icon}
      </div>
      <div style={{ color: WHITE, fontSize: 15 }}>{label}</div>
    </div>
  );
}

function DisplayActionButton({ label, marginBottom = 0 }) {
  return (
    <div
      style={{
        background: CARD_BG,
        color: WHITE,
        borderRadius: 14,
        padding: "20px 14px",
        textAlign: "center",
        fontSize: 20,
        fontWeight: 700,
        boxShadow: SHADOW,
        marginBottom,
      }}
    >
      {label}
    </div>
  );
}

function AppPreviewModal({ isOpen, onClose, gatheringID, refreshKey = 0 }) {
  const [loading, setLoading] = useState(true);
  const [appData, setAppData] = useState(null);
  const [favoriteEventIds, setFavoriteEventIds] = useState([]);
  const [routeStack, setRouteStack] = useState([{ name: "home" }]);
  const [scheduleModalEvent, setScheduleModalEvent] = useState(null);
  const [scheduleLocationFilter, setScheduleLocationFilter] = useState("All");
  const [scheduleSearch, setScheduleSearch] = useState("");
  const [showScheduleSearch, setShowScheduleSearch] = useState(false);
  const [collapsedDates, setCollapsedDates] = useState({});

  const currentRoute = routeStack[routeStack.length - 1];

  const pushRoute = useCallback((route) => {
    setScheduleModalEvent(null);
    setRouteStack((prev) => [...prev, route]);
  }, []);

  const popRoute = useCallback(() => {
    setScheduleModalEvent(null);
    setRouteStack((prev) => {
      if (prev.length > 1) {
        return prev.slice(0, -1);
      }
      return [{ name: "home" }];
    });
  }, []);

  const setRootTab = useCallback((tabName) => {
    setScheduleModalEvent(null);

    if (tabName === "home") {
      setRouteStack([{ name: "home" }]);
      return;
    }

    setRouteStack([{ name: "home" }, { name: tabName }]);
  }, []);

  const toggleFavorite = useCallback((eventId) => {
    if (!eventId) return;

    setFavoriteEventIds((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId],
    );
  }, []);

  const toggleDateCollapsed = useCallback((dateKey) => {
    setCollapsedDates((prev) => ({
      ...prev,
      [dateKey]: !prev[dateKey],
    }));
  }, []);

  const fetchSponsors = useCallback(async (currentGatheringID) => {
    try {
      const q = query(
        collection(firestore, "Sponsors"),
        where("gatheringID", "==", currentGatheringID),
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...toCamelCase(docSnap.data()),
      }));
    } catch (error) {
      console.error("Error fetching sponsors for preview:", error);
      return [];
    }
  }, []);

  const fetchPreviewData = useCallback(async () => {
    if (!gatheringID) return;

    setLoading(true);

    try {
      const [
        gathering,
        eventsRaw,
        announcementsRaw,
        mapImage,
        logoImage,
        sponsorsRaw,
      ] = await Promise.all([
        FetchGathering(gatheringID),
        FetchEvents(gatheringID),
        FetchAnnouncements(gatheringID),
        FetchMapImage(gatheringID),
        FetchLogoImage(gatheringID),
        fetchSponsors(gatheringID),
      ]);

      const vendorIDs = normalizeArray(gathering?.vendorIDs);
      const artistIDs = normalizeArray(gathering?.artistIDs);

      const [vendors, artists] = await Promise.all([
        Promise.all(
          vendorIDs.map((id) => FetchParticipant(id, "Vendor", true)),
        ),
        Promise.all(
          artistIDs.map((id) => FetchParticipant(id, "Artist", true)),
        ),
      ]);

      const cleanVendors = vendors
        .filter(Boolean)
        .map((item) => toCamelCase(item));
      const cleanArtists = artists
        .filter(Boolean)
        .map((item) => toCamelCase(item));

      const participantsByAnyKey = {};
      [...cleanVendors, ...cleanArtists].forEach((participant) => {
        const keys = [
          participant.id,
          participant.participantID,
          participant.referenceID,
          participant.gatheringVendorID,
          participant.gatheringArtistID,
          participant.vendorID,
          participant.artistID,
        ].filter(Boolean);

        keys.forEach((key) => {
          participantsByAnyKey[String(key)] = participant;
        });
      });

      const announcements = normalizeArray(announcementsRaw)
        .map((item) => toCamelCase(item))
        .sort((a, b) => {
          const aTime = a.createdAt?.seconds || a.timestamp?.seconds || 0;
          const bTime = b.createdAt?.seconds || b.timestamp?.seconds || 0;
          return bTime - aTime;
        })
        .map((item) => {
          let createdAtLabel = "";
          const seconds = item.createdAt?.seconds || item.timestamp?.seconds;

          if (seconds) {
            const date = new Date(seconds * 1000);
            createdAtLabel =
              date.toLocaleDateString(undefined, {
                month: "numeric",
                day: "numeric",
              }) +
              ", " +
              date.toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
              });
          }

          return {
            ...item,
            createdAtLabel,
          };
        });

      const events = normalizeArray(eventsRaw)
        .map((event) => toCamelCase(event))
        .map((event, index) => {
          const participantRefs = normalizeArray(
            event.artistIDs ||
              event.artistIdList ||
              event.participantIDs ||
              event.participants ||
              event.performerIDs,
          );

          const resolvedParticipants = participantRefs
            .map((ref) => {
              if (typeof ref === "object" && ref !== null) {
                return toCamelCase(ref);
              }
              return participantsByAnyKey[String(ref)] || null;
            })
            .filter(Boolean);

          return {
            ...event,
            id: event.id || `preview-event-${index}`,
            title: event.eventName || event.title || event.name || "Event",
            description: event.description || "",
            location: event.location || "Location",
            startDate: event.startDate || gathering?.startDate || "",
            participantRecords: resolvedParticipants,
            timeLabel: buildEventTimeRange(event),
          };
        })
        .sort((a, b) => {
          const aDate = `${a.startDate || ""} ${a.startTime || ""}`.trim();
          const bDate = `${b.startDate || ""} ${b.startTime || ""}`.trim();
          return aDate.localeCompare(bDate);
        });

      const initialCollapsedDates = {};
      const dateKeys = Array.from(
        new Set(events.map((event) => event.startDate || "No Date")),
      );
      dateKeys.forEach((key, index) => {
        initialCollapsedDates[key] = index !== 0;
      });

      setCollapsedDates(initialCollapsedDates);

      setAppData({
        gathering: {
          ...toCamelCase(gathering || {}),
          mapImage,
          logoImage,
        },
        announcements,
        events,
        vendors: cleanVendors,
        artists: cleanArtists,
        sponsors: sponsorsRaw,
      });
    } catch (error) {
      console.error("Error loading app preview:", error);
      setAppData(null);
    } finally {
      setLoading(false);
    }
  }, [fetchSponsors, gatheringID]);

  useEffect(() => {
    if (!isOpen) return;

    fetchPreviewData();
    setRouteStack([{ name: "home" }]);
    setScheduleModalEvent(null);
    setScheduleLocationFilter("All");
    setScheduleSearch("");
    setShowScheduleSearch(false);
  }, [isOpen, refreshKey, fetchPreviewData]);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (scheduleModalEvent) {
          setScheduleModalEvent(null);
          return;
        }

        if (routeStack.length > 1) {
          popRoute();
          return;
        }

        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose, popRoute, routeStack.length, scheduleModalEvent]);

  useEffect(() => {
    if (!document.getElementById("app-preview-hide-scrollbar-style")) {
      const style = document.createElement("style");
      style.id = "app-preview-hide-scrollbar-style";
      style.innerHTML = `
        .app-preview-screen-scroller::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const locations = useMemo(() => {
    const values = Array.from(
      new Set(
        normalizeArray(appData?.events)
          .map((event) => safeText(event.location, "").trim())
          .filter(Boolean),
      ),
    );

    return ["All", ...values];
  }, [appData]);

  const filteredEvents = useMemo(() => {
    const items = normalizeArray(appData?.events);

    return items.filter((event) => {
      const locationMatches =
        scheduleLocationFilter === "All" ||
        event.location === scheduleLocationFilter;

      const searchBlob = [
        event.title,
        event.description,
        event.location,
        ...(event.participantRecords || []).map(
          (p) => p.participantName || p.name || p.displayName,
        ),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const searchMatches =
        !scheduleSearch.trim() ||
        searchBlob.includes(scheduleSearch.trim().toLowerCase());

      return locationMatches && searchMatches;
    });
  }, [appData, scheduleLocationFilter, scheduleSearch]);

  const groupedEvents = useMemo(() => {
    const groups = {};

    filteredEvents.forEach((event) => {
      const key = event.startDate || "No Date";
      if (!groups[key]) groups[key] = [];
      groups[key].push(event);
    });

    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredEvents]);

  const goToParticipantDetail = useCallback(
    (participant) => {
      const type = safeText(
        participant?.type || participant?.participantType || "",
        "",
      ).toLowerCase();

      if (type === "artist") {
        pushRoute({ name: "artistDetail", payload: participant });
        return;
      }

      if (type === "vendor") {
        pushRoute({ name: "vendorDetail", payload: participant });
        return;
      }

      if (
        normalizeArray(appData?.artists).some(
          (a) =>
            a.id === participant.id ||
            a.referenceID === participant.referenceID,
        )
      ) {
        pushRoute({ name: "artistDetail", payload: participant });
        return;
      }

      pushRoute({ name: "vendorDetail", payload: participant });
    },
    [appData, pushRoute],
  );

  if (!isOpen) return null;

  const gathering = appData?.gathering || {};
  const latestAnnouncement = appData?.announcements?.[0];
  const logoUrl = gathering.logoImage;

  const renderHome = () => (
    <>
      <ScreenScroller>
        <div style={{ padding: "18px 20px 112px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 8,
              marginBottom: 18,
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Event logo"
                style={{
                  width: 138,
                  height: 138,
                  objectFit: "contain",
                }}
              />
            ) : (
              <div
                style={{
                  width: 138,
                  height: 138,
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 10,
                  alignItems: "center",
                  justifyItems: "center",
                }}
              >
                {[0, 1, 2, 3].map((item) => (
                  <div
                    key={item}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: DARK,
                      }}
                    />
                    <div
                      style={{
                        width: 18,
                        height: 58,
                        background: DARK,
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {latestAnnouncement ? (
            <AnnouncementCard
              item={latestAnnouncement}
              compact
              onClick={() => pushRoute({ name: "updates" })}
            />
          ) : (
            <div
              style={{
                background: CARD_BG,
                borderRadius: 14,
                color: WHITE,
                boxShadow: SHADOW,
                padding: "18px 16px",
                marginBottom: 16,
              }}
            >
              <div style={{ color: "#f0d24f", fontSize: 16, marginBottom: 6 }}>
                Announcements
              </div>
              <div style={{ fontSize: 20 }}>No announcements yet</div>
            </div>
          )}

          <div
            style={{
              marginTop: 16,
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 16,
              alignItems: "stretch",
            }}
          >
            <HomeTile
              label="Info"
              onClick={() => pushRoute({ name: "info" })}
            />
            <HomeTile
              label="Schedule"
              onClick={() => pushRoute({ name: "schedule" })}
            />
            <HomeTile label="Map" onClick={() => pushRoute({ name: "map" })} />
            <HomeTile
              label="Vendors"
              onClick={() => pushRoute({ name: "vendors" })}
            />
            <HomeTile
              label="Sponsors"
              onClick={() => pushRoute({ name: "sponsors" })}
            />
            <HomeTile
              label="Settings"
              onClick={() => pushRoute({ name: "settings" })}
            />
          </div>

          <div style={{ marginTop: 30, textAlign: "center", color: "#2380aa" }}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4 }}>
              POWERED BY
            </div>
            <img
              src={itiniriLogoLight}
              alt="itiniri"
              style={{
                display: "block",
                width: 122,
                height: "auto",
                margin: "4px auto 0",
              }}
            />
          </div>
        </div>
      </ScreenScroller>
    </>
  );

  const renderUpdates = () => (
    <>
      <ScreenScroller>
        <PhoneHeader title="Announcements" onBack={popRoute} />
        <div style={{ padding: "8px 20px 112px" }}>
          {appData?.announcements?.length ? (
            appData.announcements.map((announcement) => (
              <div
                key={announcement.id || announcement.title}
                style={{ marginBottom: 18 }}
              >
                <AnnouncementCard item={announcement} />
              </div>
            ))
          ) : (
            <div
              style={{
                marginTop: 28,
                color: TEXT,
                fontSize: 20,
                textAlign: "center",
              }}
            >
              No announcements yet
            </div>
          )}
        </div>
      </ScreenScroller>
      <BottomTabs currentTab="updates" onTab={setRootTab} />
    </>
  );

  const renderInfo = () => (
    <>
      <ScreenScroller>
        <PhoneHeader title="Info" onBack={popRoute} />
        <div style={{ padding: "12px 18px 112px" }}>
          <div
            style={{
              background: CARD_BG,
              borderRadius: 18,
              boxShadow: SHADOW,
              color: WHITE,
              padding: "20px 18px",
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 10 }}>
              {safeText(gathering.gatheringName, "Untitled Event")}
            </div>

            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              {formatDate(gathering.startDate)} -{" "}
              {formatDate(gathering.endDate)}
            </div>

            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.6)",
                opacity: 0.75,
                margin: "14px 0",
              }}
            />

            <div style={{ fontSize: 18, marginBottom: 16 }}>
              {safeText(gathering.location, "Location")}
            </div>

            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.6)",
                opacity: 0.75,
                margin: "14px 0",
              }}
            />

            <div style={{ fontSize: 17, lineHeight: 1.45 }}>
              {safeText(gathering.description, "No event description yet.")}
            </div>

            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.6)",
                opacity: 0.75,
                margin: "24px 0 16px",
              }}
            />

            <div
              style={{
                border: "1px solid rgba(255,255,255,0.45)",
                borderRadius: 16,
                padding: "18px 10px",
                display: "flex",
                justifyContent: "center",
                gap: 30,
              }}
            >
              <ContactIconButton
                label="Email"
                icon={<IconEnvelope size={25} color={WHITE} />}
              />
              <ContactIconButton
                label="Website"
                icon={<IconGlobe size={25} color={WHITE} />}
              />
            </div>
          </div>
        </div>
      </ScreenScroller>
      <BottomTabs currentTab="info" onTab={setRootTab} />
    </>
  );

  const renderSchedule = () => (
    <>
      <ScreenScroller>
        <PhoneHeader
          title="Schedule"
          onBack={popRoute}
          rightContent={
            <>
              <button
                type="button"
                aria-label="Favorites"
                style={{
                  width: 36,
                  height: 36,
                  border: 0,
                  padding: 0,
                  background: "transparent",
                  color: "#2b7da0",
                  cursor: "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconStar size={29} color="#2b7da0" />
              </button>
              <button
                type="button"
                aria-label="Search"
                onClick={() => setShowScheduleSearch((prev) => !prev)}
                style={{
                  width: 36,
                  height: 36,
                  border: 0,
                  padding: 0,
                  background: "transparent",
                  color: "#2b7da0",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconSearch size={29} color="#2b7da0" />
              </button>
            </>
          }
        />

        <div style={{ padding: "10px 18px 112px" }}>
          <div
            style={{
              display: "flex",
              gap: 10,
              overflowX: "auto",
              paddingBottom: 6,
              marginBottom: 16,
            }}
          >
            {locations.map((location) => {
              const active = scheduleLocationFilter === location;

              return (
                <button
                  key={location}
                  type="button"
                  onClick={() => setScheduleLocationFilter(location)}
                  style={{
                    whiteSpace: "nowrap",
                    minWidth: 124,
                    border: 0,
                    borderRadius: 999,
                    padding: "14px 20px",
                    background: active ? DARK : CARD_BG,
                    color: WHITE,
                    fontSize: 15,
                    boxShadow: active ? "none" : SHADOW,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  {location}
                </button>
              );
            })}
          </div>

          {showScheduleSearch && (
            <input
              value={scheduleSearch}
              onChange={(e) => setScheduleSearch(e.target.value)}
              placeholder="Search events"
              style={{
                width: "100%",
                marginBottom: 16,
                borderRadius: 12,
                border: `1px solid ${BORDER}`,
                padding: "13px 14px",
                fontSize: 15,
                color: TEXT,
                background: "rgba(255,255,255,0.68)",
                outline: "none",
              }}
            />
          )}

          {groupedEvents.length ? (
            groupedEvents.map(([dateKey, items]) => {
              const isCollapsed = !!collapsedDates[dateKey];

              return (
                <div key={dateKey} style={{ marginBottom: 18 }}>
                  <button
                    type="button"
                    onClick={() => toggleDateCollapsed(dateKey)}
                    style={{
                      width: "100%",
                      background: DARK,
                      color: WHITE,
                      border: 0,
                      borderRadius: 10,
                      padding: "16px 14px",
                      fontSize: 18,
                      fontWeight: 600,
                      marginBottom: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ color: WHITE }}>
                      {formatEventDateHeading(dateKey)}
                    </span>
                    {isCollapsed ? (
                      <IconChevronDown size={22} color={WHITE} />
                    ) : (
                      <IconChevronUp size={22} color={WHITE} />
                    )}
                  </button>

                  {!isCollapsed &&
                    items.map((event) => {
                      const isFavorite = favoriteEventIds.includes(event.id);

                      return (
                        <button
                          key={event.id}
                          type="button"
                          onClick={() => setScheduleModalEvent(event)}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            background: CARD_BG,
                            color: WHITE,
                            border: 0,
                            borderRadius: 16,
                            padding: "16px 16px 18px",
                            boxShadow: SHADOW,
                            marginBottom: 12,
                            cursor: "pointer",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 12,
                              alignItems: "flex-start",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 21,
                                fontWeight: 500,
                                marginBottom: 10,
                              }}
                            >
                              {event.title}
                            </div>
                            <div style={{ fontSize: 15, fontWeight: 700 }}>
                              {event.timeLabel}
                            </div>
                          </div>

                          <div
                            style={{
                              fontSize: 16,
                              opacity: 0.95,
                              marginBottom: 8,
                            }}
                          >
                            {event.location}
                          </div>
                          <div style={{ fontSize: 16, opacity: 0.95 }}>
                            {event.description}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              marginTop: 10,
                            }}
                          >
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(event.id);
                              }}
                              style={{
                                border: 0,
                                padding: 0,
                                background: "transparent",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <IconStar
                                size={28}
                                color="#f1d431"
                                filled={isFavorite}
                              />
                            </button>
                          </div>
                        </button>
                      );
                    })}
                </div>
              );
            })
          ) : (
            <div
              style={{
                color: TEXT,
                fontSize: 18,
                textAlign: "center",
                paddingTop: 36,
              }}
            >
              No matching events
            </div>
          )}
        </div>
      </ScreenScroller>

      {scheduleModalEvent && (
        <div
          onClick={() => setScheduleModalEvent(null)}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(17, 31, 40, 0.45)",
            zIndex: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 320,
              maxHeight: "75%",
              overflowY: "auto",
              borderRadius: 24,
              background: APP_BG,
              boxShadow: "0 24px 50px rgba(0,0,0,0.28)",
              padding: "20px 16px 20px",
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={() => setScheduleModalEvent(null)}
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                width: 42,
                height: 42,
                borderRadius: "50%",
                border: 0,
                padding: 0,
                margin: 0,
                background: CARD_BG,
                color: WHITE,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 0,
              }}
            >
              <IconClose size={20} color={WHITE} />
            </button>

            <div
              style={{
                color: TEXT,
                fontSize: 30,
                fontWeight: 800,
                textAlign: "center",
                marginTop: 8,
                marginBottom: 20,
              }}
            >
              {scheduleModalEvent.title}
            </div>

            <div
              style={{
                color: TEXT,
                fontSize: 17,
                marginBottom: 8,
                fontWeight: 800,
              }}
            >
              Description
            </div>
            <div style={{ color: TEXT, fontSize: 17, marginBottom: 14 }}>
              {safeText(
                scheduleModalEvent.description,
                "No description provided.",
              )}
            </div>

            <div
              style={{
                borderTop: `1px solid ${BORDER}`,
                margin: "12px 0 14px",
              }}
            />

            <div
              style={{
                color: TEXT,
                fontSize: 17,
                marginBottom: 8,
                fontWeight: 800,
              }}
            >
              Location
            </div>
            <div style={{ color: TEXT, fontSize: 17, marginBottom: 14 }}>
              {safeText(scheduleModalEvent.location, "Location")}
            </div>

            <div
              style={{
                borderTop: `1px solid ${BORDER}`,
                margin: "12px 0 14px",
              }}
            />

            <div
              style={{
                color: TEXT,
                fontSize: 17,
                marginBottom: 12,
                fontWeight: 800,
              }}
            >
              Participants
            </div>

            {(scheduleModalEvent.participantRecords || []).length ? (
              <>
                {scheduleModalEvent.participantRecords.map((participant) => (
                  <button
                    key={
                      participant.id ||
                      participant.referenceID ||
                      participant.participantName
                    }
                    type="button"
                    onClick={() => {
                      setScheduleModalEvent(null);
                      goToParticipantDetail(participant);
                    }}
                    style={{
                      width: "100%",
                      border: 0,
                      borderRadius: 10,
                      padding: "13px 12px",
                      background: CARD_BG,
                      color: WHITE,
                      fontSize: 17,
                      marginBottom: 10,
                      boxShadow: SHADOW,
                      cursor: "pointer",
                    }}
                  >
                    {safeText(
                      participant.participantName ||
                        participant.name ||
                        participant.displayName,
                      "Participant",
                    )}
                  </button>
                ))}
              </>
            ) : (
              <div style={{ color: TEXT, fontSize: 15 }}>
                No participants listed
              </div>
            )}
          </div>
        </div>
      )}

      <BottomTabs currentTab="schedule" onTab={setRootTab} />
    </>
  );

  const renderMap = () => (
    <>
      <ScreenScroller>
        <PhoneHeader title="Map" onBack={popRoute} />
        <div style={{ padding: "22px 20px 112px" }}>
          {gathering.mapImage ? (
            <img
              src={gathering.mapImage}
              alt="Event map"
              style={{
                width: "100%",
                borderRadius: 4,
                objectFit: "contain",
                boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
                background: "#fff",
              }}
            />
          ) : (
            <div
              style={{
                background: "rgba(255,255,255,0.7)",
                border: `1px solid ${BORDER}`,
                borderRadius: 16,
                padding: 24,
                color: TEXT,
                textAlign: "center",
                fontSize: 18,
              }}
            >
              No map uploaded yet
            </div>
          )}
        </div>
      </ScreenScroller>
      <BottomTabs currentTab="map" onTab={setRootTab} />
    </>
  );

  const renderVendors = () => (
    <>
      <ScreenScroller>
        <PhoneHeader title="Vendors" onBack={popRoute} />
        <div style={{ padding: "14px 18px 112px" }}>
          {appData?.vendors?.length ? (
            appData.vendors.map((vendor, index) => (
              <React.Fragment key={vendor.id || vendor.referenceID || index}>
                <button
                  type="button"
                  onClick={() =>
                    pushRoute({ name: "vendorDetail", payload: vendor })
                  }
                  style={{
                    width: "100%",
                    textAlign: "left",
                    border: index === 0 ? `2px solid ${DARK}` : 0,
                    borderRadius: 16,
                    background: CARD_BG,
                    color: WHITE,
                    padding: "16px 16px 18px",
                    boxShadow: SHADOW,
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{ fontSize: 24, marginBottom: 10, fontWeight: 500 }}
                  >
                    {safeText(vendor.participantName || vendor.name, "Vendor")}
                  </div>
                  <div style={{ fontSize: 16, lineHeight: 1.35 }}>
                    {safeText(
                      vendor.description || vendor.participantDescription,
                      "Vendor description",
                    )}
                  </div>
                </button>

                {index !== appData.vendors.length - 1 && (
                  <div
                    style={{
                      height: 3,
                      background: "#2b8db2",
                      margin: "16px 0",
                      opacity: 0.9,
                    }}
                  />
                )}
              </React.Fragment>
            ))
          ) : (
            <div
              style={{
                color: TEXT,
                fontSize: 18,
                textAlign: "center",
                paddingTop: 36,
              }}
            >
              No vendors yet
            </div>
          )}
        </div>
      </ScreenScroller>
      <BottomTabs currentTab="map" onTab={setRootTab} />
    </>
  );

  const renderParticipantDetail = (title, participant) => {
    const imageUrl =
      participant?.profileImage ||
      participant?.imageUrl ||
      participant?.logoImage ||
      participant?.participantImage ||
      "";

    return (
      <>
        <ScreenScroller>
          <PhoneHeader title={title} onBack={popRoute} titleSize={22} />
          <div style={{ padding: "18px 20px 112px", color: TEXT }}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={safeText(
                  participant?.participantName || participant?.name,
                  title,
                )}
                style={{
                  width: 168,
                  height: 168,
                  objectFit: "cover",
                  display: "block",
                  margin: "0 auto 16px",
                  background: "#fff",
                }}
              />
            ) : (
              <div
                style={{
                  width: 168,
                  height: 168,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                  background: CARD_BG,
                  color: WHITE,
                  fontSize: 40,
                  fontWeight: 800,
                }}
              >
                {initialsFromName(
                  participant?.participantName || participant?.name,
                )}
              </div>
            )}

            <div
              style={{
                textAlign: "center",
                fontSize: 30,
                fontWeight: 800,
                marginBottom: 22,
              }}
            >
              {safeText(
                participant?.participantName || participant?.name,
                title,
              )}
            </div>

            <div
              style={{
                border: `2px solid ${DARK}`,
                borderRadius: 18,
                padding: "16px 16px",
                marginBottom: 22,
                background: "rgba(255,255,255,0.18)",
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 8 }}>
                {title === "Artist Details"
                  ? "Artist Description"
                  : "Vendor description"}
              </div>
              <div style={{ fontSize: 16, lineHeight: 1.5 }}>
                {safeText(
                  participant?.description ||
                    participant?.participantDescription,
                  title === "Artist Details"
                    ? "Artist description"
                    : "Vendor description",
                )}
              </div>
            </div>

            <DisplayActionButton label="Visit Our Website" />
          </div>
        </ScreenScroller>
        <BottomTabs currentTab="map" onTab={setRootTab} />
      </>
    );
  };

  const renderSponsors = () => (
    <>
      <ScreenScroller>
        <PhoneHeader title="Sponsors" onBack={popRoute} titleSize={24} />
        <div style={{ padding: "14px 18px 112px" }}>
          {appData?.sponsors?.length ? (
            appData.sponsors.map((sponsor) => (
              <div
                key={sponsor.id}
                style={{
                  background: CARD_BG,
                  color: WHITE,
                  borderRadius: 16,
                  border: `2px solid ${DARK}`,
                  padding: "20px 18px",
                  boxShadow: SHADOW,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 26,
                    textAlign: "center",
                    marginBottom: 14,
                  }}
                >
                  {safeText(sponsor.name || sponsor.sponsorName, "Sponsor")}
                </div>

                <div style={{ fontSize: 16, marginBottom: 18 }}>
                  {safeText(
                    sponsor.description || sponsor.sponsorDescription,
                    "Sponsor description",
                  )}
                </div>

                <div
                  style={{ display: "flex", justifyContent: "center", gap: 30 }}
                >
                  <ContactIconButton
                    label="Email"
                    icon={<IconEnvelope size={25} color={WHITE} />}
                  />
                  <ContactIconButton
                    label="Website"
                    icon={<IconGlobe size={25} color={WHITE} />}
                  />
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                color: TEXT,
                fontSize: 18,
                textAlign: "center",
                paddingTop: 36,
              }}
            >
              No sponsors yet
            </div>
          )}
        </div>
      </ScreenScroller>
      <BottomTabs currentTab="map" onTab={setRootTab} />
    </>
  );

  const renderSettings = () => (
    <>
      <ScreenScroller>
        <PhoneHeader title="Settings" onBack={popRoute} />
        <div style={{ padding: "12px 20px 112px", color: "#2b7da0" }}>
          <div style={{ fontSize: 24, marginBottom: 10, fontWeight: 500 }}>
            Your Events
          </div>

          <div
            style={{
              background: DARK,
              color: WHITE,
              borderRadius: 10,
              padding: "16px 14px",
              textAlign: "center",
              fontSize: 16,
              marginBottom: 16,
            }}
          >
            {safeText(gathering.gatheringName, "Event")} -{" "}
            {formatDate(gathering.startDate)}
          </div>

          <div style={{ fontSize: 24, marginBottom: 10, fontWeight: 500 }}>
            Theme
          </div>
          <DisplayActionButton label="Toggle Light/Dark" marginBottom={20} />

          <div style={{ fontSize: 24, marginBottom: 10, fontWeight: 500 }}>
            Notifications
          </div>
          <DisplayActionButton label="Enable Notifications" marginBottom={20} />

          <div style={{ fontSize: 24, marginBottom: 10, fontWeight: 500 }}>
            User Actions
          </div>
          <DisplayActionButton label="Visit Our Website" marginBottom={12} />
          <DisplayActionButton label="Sign In" marginBottom={12} />
          <DisplayActionButton label="Clear All Events" marginBottom={12} />
        </div>
      </ScreenScroller>
      <BottomTabs currentTab="settings" onTab={setRootTab} />
    </>
  );

  const renderScreen = () => {
    if (loading) {
      return (
        <>
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: TEXT,
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            Loading preview...
          </div>
        </>
      );
    }

    if (!appData) {
      return (
        <>
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
              color: TEXT,
              textAlign: "center",
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            Unable to load app preview.
          </div>
        </>
      );
    }

    switch (currentRoute.name) {
      case "home":
        return renderHome();
      case "updates":
        return renderUpdates();
      case "info":
        return renderInfo();
      case "schedule":
        return renderSchedule();
      case "map":
        return renderMap();
      case "vendors":
        return renderVendors();
      case "vendorDetail":
        return renderParticipantDetail(
          "Vendor Details",
          currentRoute.payload || {},
        );
      case "artistDetail":
        return renderParticipantDetail(
          "Artist Details",
          currentRoute.payload || {},
        );
      case "sponsors":
        return renderSponsors();
      case "settings":
        return renderSettings();
      default:
        return renderHome();
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(8, 17, 24, 0.62)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 28,
          maxWidth: "min(1100px, 96vw)",
        }}
      >
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              position: "absolute",
              top: -14,
              right: -14,
              width: 42,
              height: 42,
              borderRadius: "50%",
              border: 0,
              padding: 0,
              margin: 0,
              background: "#153949",
              color: WHITE,
              boxShadow: SHADOW,
              cursor: "pointer",
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 0,
            }}
          >
            <IconClose size={20} color={WHITE} />
          </button>

          <PhoneShell>{renderScreen()}</PhoneShell>
        </div>

        <div
          style={{
            width: 320,
            maxWidth: "32vw",
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${BORDER}`,
            borderRadius: 18,
            boxShadow: SHADOW,
            padding: "22px 20px",
            color: TEXT,
            lineHeight: 1.5,
            fontSize: 16,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 10 }}>
            Preview Notice
          </div>
          <div>
            Please note that this preview is not a one-to-one representation of
            the app. Issues may arise if viewed on smaller screens, and some
            features are disabled. To view your event on a mobile device,
            download the app and sign in using the email you used during account
            creation, then add your event shortcode.
          </div>
          <div>
            If the preview is not showing your current event information, try
            refreshing this webpage and opening the preview again.
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppPreviewModal;
